import type { IBankingBlockResponseMap, IBankingRegion } from './types'

export const CAPITAL_REGION = 'bishkek'

// The "capital saves, regions borrow" story holds only while the capital keeps most deposits
const CAPITAL_MAJORITY_SHARE = 50

export interface IBankingRegionsStory {
  capital: IBankingRegion
  leader: IBankingRegion
}

/**
 * Regions block data shared by the hero fact and the regions block, so both read one request
 * and one rule for the story they tell.
 */
export const useBankingRegions = () => {
  const request = useApi<IBankingBlockResponseMap['regions']>('/banking-statistics/blocks/regions', {
    key: 'banking-statistics-regions',
    // Keeps the unused region history out of the page payload.
    transform: ({ data }) => ({ data: { as_of: data.as_of, regions: data.regions } })
  })

  const regions = computed<IBankingRegion[]>(() => request.data.value?.data?.regions ?? [])
  const asOf = computed(() =>
    regions.value.length ? (request.data.value?.data?.as_of ?? null) : null
  )

  /** Capital and the region with the most credits per som of deposits, or null when the story does not hold. */
  const story = computed<IBankingRegionsStory | null>(() => {
    const capital = regions.value.find((region) => region.key === CAPITAL_REGION)
    const leader = regions.value.reduce<IBankingRegion | null>(
      (best, region) =>
        best === null || region.credits_per_deposit > best.credits_per_deposit ? region : best,
      null
    )

    if (
      !capital ||
      !leader ||
      leader.key === capital.key ||
      leader.credits_per_deposit <= 1 ||
      capital.deposits_share < CAPITAL_MAJORITY_SHARE
    ) {
      return null
    }

    return { capital, leader }
  })

  return { request, regions, asOf, story }
}
